import { createContext, useContext, useState, useCallback } from 'react'
import type { ReactNode } from 'react'
import { SUPPORTED_LOCALES, TRANSLATIONS } from './translations'
import type { Locale, Translations } from './translations'

const LOCALE_STORAGE_KEY = 'investfacil-locale'

export function normalizeLocale(value: string): Locale {
  const lowered = value.toLowerCase()
  if (lowered.startsWith('pt')) return 'pt-BR'
  return 'en'
}

export function detectInitialLocale(): Locale {
  // 1. Check localStorage preference
  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY)
    if (SUPPORTED_LOCALES.some((locale) => locale === stored)) {
      return stored as Locale
    }
    if (stored) {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, 'en')
      return 'en'
    }
  } catch {
    // Continue with browser language detection when storage is unavailable.
  }

  // 2. Match browser language preferences (e.g., es-MX, pt-PT)
  const preferredLanguages = navigator.languages?.length
    ? navigator.languages
    : [navigator.language]
  for (const candidate of preferredLanguages) {
    if (typeof candidate !== 'string' || !candidate) continue
    const normalized = normalizeLocale(candidate)
    if (normalized !== 'en') return normalized
  }

  // 3. Fallback
  return 'en'
}

interface I18nContextValue {
  locale: Locale
  t: Translations
  setLocale: (locale: Locale) => void
}

const I18nContext = createContext<I18nContextValue | null>(null)

export function I18nProvider({ children }: { children: ReactNode }): JSX.Element {
  const [locale, setLocaleState] = useState<Locale>(detectInitialLocale)

  const setLocale = useCallback((next: Locale): void => {
    setLocaleState(next)
    try {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, next)
    } catch {
      // non-fatal
    }
  }, [])

  return (
    <I18nContext.Provider value={{ locale, t: TRANSLATIONS[locale], setLocale }}>
      {children}
    </I18nContext.Provider>
  )
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext)
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>')
  return ctx
}
